import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Client, clientsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface Props {
  source: Client;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (target: Client) => void;
}

export function ChangeCnpjDialog({ source, open, onOpenChange, onSuccess }: Props) {
  const [query, setQuery] = useState("");
  const [targetId, setTargetId] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["clients", "cnpj-change"],
    queryFn: () => clientsApi.getAll(),
    enabled: open,
  });
  const candidates = useMemo(() => {
    const value = query.trim().toLowerCase();
    return (data?.data ?? []).filter((client) => client.clientId !== source.clientId)
      .filter((client) => !value || [client.clientCompanyName, client.clientFantasyName, client.clientCodeCnx, client.clientCnpj]
        .some((field) => String(field ?? "").toLowerCase().includes(value)))
      .slice(0, 20);
  }, [data, query, source.clientId]);
  const selected = data?.data.find((client) => client.clientId === targetId);

  const submit = async () => {
    if (!source.clientId || !targetId || !selected || !reason.trim()) return;
    setIsSubmitting(true);
    try {
      const result = await clientsApi.changeCnpj(source.clientId, targetId, reason.trim());
      toast.success("Troca de CNPJ concluída", {
        description: `${result.scheduledOrdersMoved} programação(ões) movida(s) e ${result.profilesCreated} produto(s) replicado(s).`,
      });
      onOpenChange(false);
      onSuccess(selected);
    } catch (error: any) {
      toast.error("Não foi possível trocar o CNPJ", {
        description: error?.response?.data?.message ?? "Revise o cadastro de destino e tente novamente.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Trocar CNPJ da escola</DialogTitle>
        <DialogDescription>O destino deve ser um cadastro novo, sem produtos. Ele será ativado, receberá os produtos da origem e assumirá toda a programação pendente.</DialogDescription>
      </DialogHeader>
      <div className="rounded-md border p-3 text-sm">
        <div className="font-medium">Origem: {source.clientCompanyName}</div>
        <div className="text-muted-foreground">{source.clientCodeCnx} · {source.clientCnpj}</div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="target-client-search">Cliente de destino</Label>
        <Input id="target-client-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Busque por escola, CNXC ou CNPJ" />
        <div className="max-h-48 overflow-y-auto rounded-md border">
          {isLoading && <div className="p-3 text-sm text-muted-foreground">Carregando clientes...</div>}
          {!isLoading && candidates.length === 0 && <div className="p-3 text-sm text-muted-foreground">Nenhum cliente encontrado.</div>}
          {candidates.map((client) => <button type="button" key={client.clientId} onClick={() => setTargetId(client.clientId ?? null)}
            className={`block w-full border-b p-3 text-left text-sm last:border-b-0 ${targetId === client.clientId ? "bg-accent" : "hover:bg-muted"}`}>
            <div className="font-medium">{client.clientCompanyName}</div>
            <div className="text-muted-foreground">{client.clientCodeCnx} · {client.clientCnpj}</div>
          </button>)}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="cnpj-change-reason">Motivo da troca</Label>
        <Textarea id="cnpj-change-reason" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Informe o motivo para manter a operação auditável" />
      </div>
      <p className="text-sm text-amber-700 dark:text-amber-400">A operação será bloqueada se o destino já tiver produtos, se houver pedido em processamento ou programação conflitante.</p>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>Cancelar</Button>
        <Button onClick={submit} disabled={!targetId || !reason.trim() || isSubmitting}>{isSubmitting ? "Processando..." : "Confirmar troca"}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
